<template>
  <modal
    elem-id="modal_grace_revoke"
    :title="t('Revogar carência')"
    :show="isVisible"
    size="small"
    :close-label="t('Cancelar')"
    :save-label="t('Revogar')"
    :save-fn="handleSave"
    :save-disabled-fn="() => isSaving"
    save-test-id="grace-revoke-submit"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <div class="revoke-context">
        <div class="context-field">
          <span class="context-label">{{ t('Franquia') }}</span>
          <span class="context-value" data-testid="grace-revoke-franchise">{{ currentRow?.franchise_name }}</span>
        </div>
        <div class="context-field">
          <span class="context-label">{{ t('Fim') }}</span>
          <span class="context-value" data-testid="grace-revoke-ends-at">{{ endsAtDisplay }}</span>
        </div>
      </div>
      <div class="revoke-input">
        <input-text
          v-model="reason"
          elem-id="grace-revoke-reason"
          :label="t('Motivo') + ' (' + t('opcional') + ')'"
          data-testid="grace-revoke-reason-input"
        />
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useGraceAdmin } from '../../../composables/useGraceAdmin'
import { useDateFormatter } from '../../../composables/useDateFormatter'
import Modal from '../../../ui/Modal.vue'
import InputText from '../../../inputs/InputText.vue'
import type { Grace_PeriodRow } from '../../../contracts/Grace_PeriodRow'

interface ModalOpenPayload {
  row?: Grace_PeriodRow
}

const { t } = useI18n()
const { revoke } = useGraceAdmin()
const { formatDate, fromApiDateToDate } = useDateFormatter()

const isVisible = ref(false)
const isSaving = ref(false)
const currentRow = ref<Grace_PeriodRow | null>(null)
const reason = ref<string>('')

const endsAtDisplay = computed((): string => {
  const raw = currentRow.value?.ends_at ?? null
  if (!raw) return ''
  const d = fromApiDateToDate(raw)
  return formatDate(d)
})

function openModal(payload?: ModalOpenPayload): void {
  log('GraceRevokeModal', 'openModal', 'Abrindo modal de revogação', payload)
  currentRow.value = payload?.row ?? null
  reason.value = ''
  isVisible.value = true
}

function closeModal(): void {
  log('GraceRevokeModal', 'closeModal', 'Fechando modal de revogação')
  isVisible.value = false
  currentRow.value = null
  reason.value = ''
}

async function handleSave(): Promise<void> {
  const row = currentRow.value
  if (!row) return
  isSaving.value = true
  try {
    await revoke({
      grace_id: row.grace_id,
      ...(reason.value ? { reason: reason.value } : {}),
    })
    log('GraceRevokeModal', 'handleSave', 'Carência revogada', { grace_id: row.grace_id })
    eventBus.emit('globalMessage.success', t('Carência revogada com sucesso'))
    eventBus.emit('graceRevoke.saved')
    closeModal()
    eventBus.emit('grace.refresh')
  } catch (err) {
    // Surface the backend's NAMED refusal. POST /subscriptions/grace/revoke
    // answers 'grace_not_found', 'grace_not_revocable' or a validation message
    // like 'grace_id must be a positive integer' — each says something
    // different and actionable. Collapsing all three into one generic sentence
    // is how a caller sending grace_id=0 looked identical to an already-expired
    // window, and the real cause stayed invisible for the whole session
    // (2026-08-21).
    log('GraceRevokeModal', 'handleSave', 'Erro ao revogar carência', err)
    const reason = err instanceof Error ? err.message : ''
    eventBus.emit(
      'globalMessage.error',
      reason ? `${t('Erro ao revogar carência')}: ${reason}` : t('Erro ao revogar carência'),
    )
  } finally {
    isSaving.value = false
  }
}

onMounted(() => {
  eventBus.on('modal.graceRevoke.open', openModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.graceRevoke.open', openModal)
})
</script>

<style scoped>
.revoke-context {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
}

.context-field {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.context-label {
  font-weight: 600;
  min-width: 80px;
}

.revoke-input {
  margin-top: 0.5rem;
}
</style>
